import { Query, useQuery } from '@tanstack/react-query';
import { getAllFunnelContactsNew } from 'API/linkedin-funnel-search';
import { AxiosError } from 'axios';
import { FunnelContact } from 'features/co-pilot/co-pilot.types';
import { useDebounce } from 'hooks/use-debounce';
import { useUserData } from 'hooks/use-user-data';
import { useEffect, useMemo } from 'react';
import useErrorStore, { CustomErrorClass } from 'stores/error-store';
import { ContactFilter, Qualifications } from '../../campaign-builder.types';
import { buildAiJudgementParam } from './use-contacts.logic';

interface ContactsResponse {
  funnelContacts: FunnelContact[];
  total: number;
  isProcessing: boolean;
  isProcessingProfileAnalysis?: boolean;
}

interface Params {
  aiJudgement?: string;
  filter?: ContactFilter;
  limit?: number;
  offset?: number;
  q?: string;
}

const DEBOUNCE_DELAY = 500;
const QUICK_REFRESH_INTERVAL = 1 * 1000;
const REFRESH_INTERVAL = 10 * 1000;
const STALE_TIME = 2 * 60 * 1000;

const useContacts = (
  campaignId: string | undefined = '',
  hasJudgingSettings: boolean,
  allowed: boolean,
  isSearchRunning: boolean,
  contactFilter: ContactFilter,
  activeQualifications: Qualifications[],
  contactsPage: number,
  contactsSearchParam: string,
  contactsPerPage: number,
  resetContactsPage: () => void
) => {
  const { token } = useUserData();
  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const debouncedContactsSearchParam = useDebounce(contactsSearchParam, DEBOUNCE_DELAY);

  const params: Params = useMemo(() => {
    return {
      aiJudgement: buildAiJudgementParam(activeQualifications, hasJudgingSettings),
      filter: !contactFilter ? undefined : contactFilter,
      limit: contactsPerPage,
      offset: contactsPerPage * (contactsPage - 1),
      q: debouncedContactsSearchParam || undefined,
    };
  }, [contactsPage, contactsPerPage, debouncedContactsSearchParam, contactFilter, activeQualifications, hasJudgingSettings]);

  const { data, isLoading, isError, error } = useQuery<ContactsResponse>({
    queryKey: ['contacts', token, campaignId, params],
    queryFn: ({ signal }) => getAllFunnelContactsNew(campaignId, params, signal),
    enabled: !!token && !!campaignId && !!params && allowed,
    staleTime: STALE_TIME,
    refetchInterval: isSearchRunning
      ? QUICK_REFRESH_INTERVAL
      : (query: Query<ContactsResponse, Error>) =>
          query.state.data?.isProcessing || query.state.data?.isProcessingProfileAnalysis ? REFRESH_INTERVAL : false,
  });

  useEffect(() => {
    if (isError) {
      const customError = new CustomErrorClass(error as AxiosError);
      handleOpenErrorDialog(customError, customError.request, customError.requestType);
    }
  }, [isError, error]);

  useEffect(() => {
    resetContactsPage();
  }, [debouncedContactsSearchParam, contactFilter, activeQualifications]);

  return {
    contacts: data?.funnelContacts || [],
    totalContacts: data?.total || 0,
    aiJudgementProcessing: data?.isProcessing || false,
    profileAnalysisProcessing: data?.isProcessingProfileAnalysis || false,
    isLoading,
    isError,
    error,
  };
};

export default useContacts;
